const METERS_PER_MILE = 1609;
const MAX_RADIUS_MILES = 5;
const MAX_CARD_COUNT = 12;
const TYPE_LABELS = {
  school: "Escolas",
  supermarket: "Supermercado",
  gas_station: "Postos",
  pharmacy: "Farmácias",
  hospital: "Hospitais",
  unknown: "Utilidade",
};
const SEARCH_TYPES = Object.keys(TYPE_LABELS).filter((type) => type !== "unknown");

function formatSyncedAt(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString("pt-BR", {
      hour12: false,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function getTypeLabel(type) {
  if (!type) return "Utilidade";
  return TYPE_LABELS[type] || type;
}

function buildMapUrl(poi) {
  const lat = poi?.geometry?.location?.lat;
  const lng = poi?.geometry?.location?.lng;
  if (lat !== undefined && lat !== null && lng !== undefined && lng !== null) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${lat},${lng}`,
    )}`;
  }

  if (poi?.place_id) {
    return `https://www.google.com/maps/search/?api=1&query=place_id:${encodeURIComponent(
      poi.place_id,
    )}`;
  }

  return "https://www.google.com/maps";
}

function buildPhotoUrl(photoReference) {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!photoReference || !key) return null;
  return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=260&photoreference=${photoReference}&key=${key}`;
}

export default function PropertyGooglePlacesInsights({ snapshot }) {
  if (!snapshot) return null;

  const results = Array.isArray(snapshot.results) ? snapshot.results : [];
  const aggregatedResults =
    results.length && results.every((entry) => entry?.type && Array.isArray(entry.items))
      ? results
      : [
          {
            type: "unknown",
            radius_meters: snapshot.radius_meters,
            google_status: snapshot.google_status,
            items: results,
          },
        ];

  const totalPois = aggregatedResults.reduce(
    (acc, entry) => acc + (Array.isArray(entry?.items) ? entry.items.length : 0),
    0,
  );

  const typeSummaries = SEARCH_TYPES.map((type) => {
    const entry = aggregatedResults.find((item) => item?.type === type);
    const count = Array.isArray(entry?.items) ? entry.items.length : 0;
    const radiusMiles = entry?.radius_meters
      ? Math.max(1, Math.round(entry.radius_meters / METERS_PER_MILE))
      : null;

    return {
      type,
      count,
      radiusMiles,
      status: entry?.google_status,
    };
  });

  const hasUnknown = aggregatedResults.some((entry) => entry?.type === "unknown");

  if (hasUnknown) {
    typeSummaries.push({
      type: "unknown",
      count: aggregatedResults
        .filter((entry) => entry?.type === "unknown")
        .reduce(
          (acc, entry) => acc + (Array.isArray(entry?.items) ? entry.items.length : 0),
          0,
        ),
      radiusMiles: snapshot.radius_meters
        ? Math.max(1, Math.round(snapshot.radius_meters / METERS_PER_MILE))
        : MAX_RADIUS_MILES,
      status: snapshot.google_status,
    });
  }

  const pois = aggregatedResults.flatMap((entry) =>
    (Array.isArray(entry?.items) ? entry.items : []).map((item) => ({
      ...item,
      metadataType: entry?.type,
    })),
  );

  const displayPois = pois.slice(0, MAX_CARD_COUNT);
  const moreCount = totalPois - displayPois.length;
  const syncedAt = formatSyncedAt(snapshot.created_at);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-4">
      <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            Nearby Points of Interest
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Buscas segmentadas por tipo até 5 milhas
          </div>
        </div>
        {syncedAt && (
          <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
            Última sincronização
            <br />
            {syncedAt}
          </div>
        )}
      </div>

      {typeSummaries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {typeSummaries.map((summary) => (
            <div
              key={summary.type}
              className="rounded-lg border border-gray-100 dark:border-gray-800/60 bg-gray-50 dark:bg-gray-900/40 p-3 text-center"
            >
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {getTypeLabel(summary.type)}
              </div>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {summary.count}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                até {summary.radiusMiles ?? MAX_RADIUS_MILES} milhas
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPois === 0 ? (
        <div className="text-sm text-gray-600 dark:text-gray-300">
          Nenhum ponto de interesse foi retornado durante a última busca.
        </div>
      ) : (
        <div className="space-y-3">
          {displayPois.map((poi) => {
            const photoRef = poi.photos?.[0]?.photo_reference;
            const photoUrl = buildPhotoUrl(photoRef);
            const mapUrl = buildMapUrl(poi);

            return (
              <a
                key={poi.place_id || `${poi.name}-${poi.vicinity}`}
                href={mapUrl}
                target="_blank"
                rel="noreferrer"
                className="group flex items-stretch gap-3 rounded-lg border border-gray-100 dark:border-gray-800/60 bg-gray-50 dark:bg-gray-900/40 p-3 transition hover:border-gray-300 hover:dark:border-gray-600"
              >
                {photoUrl ? (
                  <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md bg-gray-200 dark:bg-gray-800">
                    <img
                      src={photoUrl}
                      alt={`Foto de ${poi.name}`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-md bg-gray-200 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    Sem foto
                  </div>
                )}

                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {poi.name}
                    </h3>
                    <span className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      {getTypeLabel(poi.metadataType)}
                    </span>
                  </div>
                  {poi.vicinity && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {poi.vicinity}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>
                      {poi.rating
                        ? `${poi.rating.toFixed(1)} ★`
                        : "Sem avaliação"}
                    </span>
                    {poi.user_ratings_total && (
                      <span>• {poi.user_ratings_total} avaliações</span>
                    )}
                  </div>
                  {poi.opening_hours?.open_now && (
                    <div className="text-xs font-semibold text-green-600 dark:text-green-400">
                      Aberto agora
                    </div>
                  )}
                </div>
              </a>
            );
          })}

          {moreCount > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Mostrando {displayPois.length} de {totalPois} resultados. Clique na
              aba para abrir o endereço no Google Maps.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
